import { boxShadow } from '@foxtons/fdl/theme/colorPalette';
import { SxProps } from '@mui/material';
import theme, { globalSectionPadding } from '@theme/index';

const { palette, spacing, containers, typography } = theme;

const styles: Record<string, SxProps> = {
  paginationWrapper: {
    '& p': {
      marginTop: spacing(1)
    }
  },

  notFoundContainer: {
    height: '50vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    ...containers.type2,
    '& h3': {
      color: palette.primary.main,
      textAlign: 'center',
      marginBottom: spacing(1)
    },
    '& h4': {
      color: palette.grey.main,
      textAlign: 'center'
    },
    '& h5': {
      color: palette.grey.main,
      textAlign: 'center',
      mb: spacing(0.5)
    },
    '& svg': {
      fontSize: '3.5rem',
      color: palette.primary.main
    },
    '& a:nth-of-type(1)': {
      textDecoration: 'none',
      my: spacing(0.5),
      color: palette.primary.main
    },
    '& a:nth-of-type(2)': {
      color: palette.primary.main,
      textDecoration: 'none',
      my: '5.25rem'
    },
    backgroundColor: palette.lightishAqua['400']
  },
  notFoundIconBox: {
    background: palette.gradient['700'],
    borderRadius: '50%',
    padding: spacing(1),
    marginBottom: spacing(1)
  },
  mainContainer: {
    mt: '6.75rem'
  },
  cardBox: {
    gap: '1rem',
    mt: '0.5rem',
    padding: globalSectionPadding,
    paddingTop: {
      xs: '0',
      md: '0'
    }
  },
  propertyListWrapper: {
    display: 'grid',
    width: '100%',
    justifyContent: 'center',
    gridTemplateColumns: {
      // Rob - These rem values are the card width values, should be a variable really
      xs: 'repeat(auto-fit, 21rem)',
      sm: 'repeat(auto-fit, 19.25rem)',
      gap: spacing(1)
    }
  },
  propertyBox: {
    height: '100%'
  },
  propertyNotAvailableContainer: {
    px: globalSectionPadding
  },
  propertyNotAvailableInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    py: '4rem',
    '& h3': {
      color: palette.primary.main,
      textAlign: 'center',
      marginBottom: '1rem'
    },
    '& p': {
      marginBottom: '1.25rem'
    }
  },
  buttonTransform: {
    marginTop: spacing(2),
    textTransform: 'inherit'
  },
  sectionHeading: {
    marginTop: spacing(2),
    marginBottom: spacing(1),
    color: palette.primary.main
  },
  recommendedLegendsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: spacing(1),
    my: spacing(1)
  },
  legendTextIcon: {
    display: 'flex',
    columnGap: '0.5rem'
  },
  // using !important to override default map styles
  mapStyles: {
    '.gmnoprint:not([role=menubar])': {
      top: {
        xs: '12rem !important',
        md: '4rem !important',
        xl: '13rem !important'
      },
      display: 'flex',
      flexDirection: 'column-reverse'
    },
    '.gm-bundled-control-on-bottom': {
      bottom: '1rem !important'
    },
    '.gm-svpc': {
      top: {
        xs: '9rem !important',
        md: '10rem !important'
      },
      mb: '6rem !important'
    },
    width: '100%',
    height: '100%'
  },
  infoBox: {
    borderRadius: 2,
    backgroundColor: palette.primary[400],
    width: '100%',
    marginRight: {
      xs: '0rem',
      lg: '0.5rem'
    },
    marginTop: '-1.25rem',
    overflow: 'hidden'
  },
  stationMapBox: {
    borderRadius: 2,
    backgroundColor: palette.primary[400],
    width: '100%',
    pl: '4rem',
    pr: '4rem',
    overflow: 'hidden'
  },
  infoHeading: {
    fontWeight: '700',
    fontSize: {
      xs: '0.875rem',
      md: '1.25rem'
    },
    pt: '0.875rem',
    pl: '0.875rem',
    pr: '0.875rem',
    color: palette.darkestBlack.main
  },
  stationHeading: {
    fontWeight: '700',
    fontSize: '1.25rem',
    pt: '0.875rem',
    pl: '0.875rem',
    pr: '0.875rem',
    color: palette.darkestBlack.main
  },
  narrowSearchWrapper: {
    padding: '0 !important'
  },
  narrowSearchFullScreenWrapper: {
    my: spacing(0.5),
    mx: spacing(1)
  },
  infoText: {
    fontWeight: '500',
    fontSize: {
      xs: '0.875rem'
    },
    pt: {
      md: '0.875rem'
    },
    pl: '0.875rem',
    pr: '0.875rem',
    pb: '0.875rem',
    color: palette.darkestBlack.main
  },
  borderLine: {
    height: '0.063rem',
    backgroundColor: palette.secondary[600],
    mt: '1.5rem',
    ml: '0.875rem',
    mr: '0.875rem'
  },
  stationLinesImagesBox: {
    display: 'flex',
    flexDirection: 'row',
    ml: 'auto',
    mr: 'auto',
    justifyContent: 'center'
  },
  stationLineImage: {
    width: '100%',
    position: 'relative'
  },
  stationBoxTop: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  stationDropDownContainer: {
    mt: '1.5rem',
    mr: '1rem',
    minWidth: '10.5rem'
  },
  stationSelectBox: {
    height: '2.25rem',
    borderColor: palette.secondary[600],
    backgroundColor: '#FFFFFF',
    '& .MuiSelect-icon': {
      color: palette.gradient.main
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.secondary[600]
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.secondary[600]
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.secondary[600]
    },
    '&.Mui-selected': {
      backgroundColor: palette.primary[400]
    },
    '&.MuiMenu-paper': {
      fontWeight: {
        xs: 500,
        md: 700
      },
      fontSize: '0.75rem'
    }
  },
  stationMuiList: {
    '& .MuiMenu-list': {
      padding: 0
    },
    '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
      backgroundColor: palette.primary[400]
    },
    '& .MuiMenuItem-root': {
      minHeight: '2.75rem',
      display: 'flex',
      alignItems: 'center'
    }
  },
  stationMenuItem: {
    fontWeight: 400,
    fontSize: '1rem'
  },
  hyperLink: {
    fontWeight: 500,
    fontSize: '0.75rem',
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center'
  },
  stationLineOverlappingContainer: {
    position: 'absolute',
    top: 0,
    width: '33.3333333333%',
    height: '100%'
  },
  stationLinesTextContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%'
  },
  textWrapper: {
    width: '8rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
    '& > button': {
      textDecoration: 'none'
    }
  },
  dialogBox: {
    backgroundColor: palette.grey['50'],
    borderRadius: '0px',
    width: {
      xs: '90%',
      sm: '60%',
      md: '40%',
      lg: '35%',
      ml: '25%'
    },
    paddingY: '1.5rem',
    position: 'fixed',
    height: 'auto',
    boxShadow: boxShadow[50],
    bottom: '35%',
    left: {
      xs: '5%',
      md: '37%'
    },
    zIndex: 1000000
  },
  dialogTitleContent: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${palette.lightishBluish.main}`,
    paddingX: {
      xs: '1rem',
      md: '1.3rem'
    },
    paddingBottom: '1rem'
  },
  dialogTitle: {
    fontWeight: '700',
    fontSize: {
      xs: '1rem',
      md: '1.5rem'
    },
    color: palette.grey['500']
  },
  dialogContent: {
    paddingX: '1.5rem',
    marginTop: '1.3rem'
  },
  dialogSubTitle: {
    fontWeight: '400',
    fontSize: '1rem',
    color: palette.grey.main
  },
  editButton: {
    marginTop: '1.5rem',
    width: '100%',
    textTransform: 'none'
  },
  increaseCommuntingTimeBtn: {
    mt: '1.2rem',
    width: {
      xs: '100%',
      md: '50%'
    }
  },
  increaseCommuntingTimeBox: {
    mt: {
      xs: '0.8rem',
      md: '1.2rem'
    }
  },
  noResultMapAndResultBox: {
    display: 'flex',
    marginX: {
      xs: '1rem',
      md: '5rem'
    },
    mb: {
      xs: '1rem',
      md: '0'
    }
  },
  noResultFoundHeight: {
    height: {
      xs: '60.5vh',
      md: '70.5vh'
    }
  },
  listAndMapViewBox: {
    justifyContent: { xs: 'center', lg: 'space-between' },
    marginTop: {
      xs: '-1.5rem',
      md: '-1.5rem'
    },
    marginLeft: { md: spacing(1) },
    marginRight: { md: spacing(1) }
  },
  stationLinesWrapper: {
    marginTop: '1.5rem'
  },
  noResultRecommendedProperties: {
    pt: '1rem',
    paddingX: {
      xs: '1.5rem',
      md: '5rem'
    }
  },
  recommendedSection: {
    paddingX: { sm: 0, md: spacing(1) },
    paddingY: spacing(2)
  }
};

export const searchLocationContentStyle: Record<string, SxProps> = {
  RichTextSection: {
    width: '100%',
    '& > div, & > div > div': {
      padding: 0
    },
    '& h2': {
      ...typography.h5,
      marginBottom: spacing(1),
      color: palette.darkestBlack.main
    }
  },
  AccordionWithTitle: {
    width: '100%',
    '& > div': {
      padding: 0
    }
  }
};

export default styles;
